<script setup lang="ts">
//#region imports
import { computed, onMounted, ref } from 'vue';
import FilterTabs from '@/components/FilterTabs.vue';
import { useDashboardFilter } from '@/composables/useDashboardFilter';
import { useLodash } from '@/setup/lodash';
import type { FilterOp, FilterRecord } from '@/types/core';
import type { OperatorListItem } from '@/components/UIElements/UiTabs.vue';
//#endregion

//#region types
export type AllFilterKeys = keyof typeof availableFilters.value;

export type DefaultFilterListType = typeof defaultFilterList;
export type DefaultFilterListKeyType = keyof typeof defaultFilterList;

export interface CurrentFilter {
  type: AllFilterKeys;
  values: any;
  op: FilterOp | null;
}

export interface Props {
  disabledMode?: boolean,
  setCurrentSearch: FilterRecord,
}

export interface Emits {
  'changeFilterTab': [search: FilterRecord],
  'changeFilter': [filters: CurrentFilter[]],
  'isDisableSelectFilter': [disable: boolean],
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  disabledMode: false,
});
const emit = defineEmits<Emits>();
const modelValue = defineModel<CurrentFilter[]>({
  default: () => [] as CurrentFilter[],
});
//#endregion

//#region const
const operatorList: Readonly<OperatorListItem>[] = [
  {
    value: '=',
    name: '=',
  },
  {
    value: '!=',
    name: '≠',
  },
];

const defaultFilterList = {
  site_player_id: {
    icon: 'id-badge',
    order: 1,
  },
  affiliate_email: {
    icon: 'user',
    order: 2,
  },
  sub_affiliate_email: {
    icon: 'users',
    order: 3,
  },
  referrer_domain: {
    icon: 'globe',
    order: 4,
  },
} as const;
//#endregion

//#region composables
const {
  handleDisabled,
  isDisabled,
  availableFilters,
  isSelectLoading,
  reportsSettings,
}  = useDashboardFilter();
const _ = useLodash();

const excludeFilters = computed(() => {
  return _.map(modelValue.value, (filter) => filter.type);
});
//#endregion

//#region state
const currentSearch = ref(_.cloneDeep(props.setCurrentSearch));
//#endregion

//#region computed
const searchByList = computed(() => {
  return reportsSettings.value.dimensions.reduce((acc, el) => {
    if (defaultFilterList.hasOwnProperty(el.column_name)) {
      acc[el.column_name as keyof typeof defaultFilterList]
        = defaultFilterList[el.column_name as keyof typeof defaultFilterList];
    }
    return acc;
  }, {} as Record<keyof typeof defaultFilterList, typeof defaultFilterList[keyof typeof defaultFilterList]>);
});

const isSearchingFilter = computed(() => {
  const filtersList = Object.keys(searchByList.value);
  return filtersList.reduce((acc, elem) => {
    acc[elem] = _.has(currentSearch.value, elem);
    return acc;
  }, {} as Record<string, any>);
});
//#endregion

//#region methods


function addFilter() {
  modelValue.value!.push({
    type: '' as AllFilterKeys,
    values: [],
    op: null,
  });
  handleDisabled(modelValue.value);
  emit('isDisableSelectFilter', isDisabled.value);
}

function removeFilter(i: number) {
  modelValue.value!.splice(i, 1);
  handleDisabled(modelValue.value);
  fillFilter();
  emit('isDisableSelectFilter', isDisabled.value);
}

function inputSelect(v: string, index: number) {
  const noParse = ['trafficType', 'playerFilter'];

  if (!noParse.includes(v)) {
    modelValue.value![index]!['op'] = '=';
  } else {
    modelValue.value![index]!['op'] = null;
  }
}

function fillFilter() {
  const typeNotReady = (val: CurrentFilter[]) => val.every(
    (ev) => !_.isEmpty(ev.values) && !_.isEmpty(ev.type),
  );

  if (typeNotReady(modelValue.value)) {
    handleDisabled(modelValue.value);
    emit('isDisableSelectFilter', isDisabled.value);
    emit('changeFilter', modelValue.value);
  }
}

function clearReportsSearchFilter(group: string) {
  delete currentSearch.value[group];
  emit('changeFilterTab', currentSearch.value);
}

function selectedSearchFilter(search: any) {
  currentSearch.value = _.cloneDeep({ ...currentSearch.value, ...search });
  emit('changeFilterTab', currentSearch.value);
}
//#endregion

//#region hooks
onMounted(() => {
  handleDisabled(modelValue.value);
  emit('isDisableSelectFilter', isDisabled.value);
});
//#endregion
</script>

<template>
  <div class="dashboard-filter">
    <div
      v-for="(item, index) in modelValue"
      :key="index"
      class="dashboard-filter__row t:space-x-8"
    >
      <UiButton
        type="negative"
        icon="minus"
        is-icon
        :disabled="disabledMode"
        @click="removeFilter(index)"
      />
      <UiSelectElement
        v-model="item.type"
        :list="availableFilters || []"
        :placeholder="$t('ui.select.default')"
        :disabled="disabledMode"
        :exclude-options="excludeFilters"
        size="medium"
        option-label-param="name"
        option-value-param="type"
        @change="inputSelect($event, index), item.values = availableFilters[item.type].multiple ? [] : ''"
      />
      <UiTabs
        v-if="item.op !== null"
        v-model="item.op"
        :disabled="disabledMode"
        :list="operatorList"
        @change="fillFilter"
      />
      <UiSelectElement
        v-model="item.values"
        :loading="isSelectLoading"
        :list="availableFilters[item.type]?.options()"
        :option-label-param="availableFilters[item.type]?.labelProp"
        :option-name-param="availableFilters[item.type]?.labelProp"
        :option-value-param="availableFilters[item.type]?.valueProp"
        :remote="availableFilters[item.type]?.remote"
        :remote-method="availableFilters[item.type]?.method"
        :disabled="!item.type || disabledMode"
        :multiple="availableFilters[item.type]?.multiple"
        :filterable="availableFilters[item.type]?.filterable"
        collapse-tag-tooltip
        :max-collapse-tags="1"
        @change="fillFilter"
      />
    </div>
    <UiButton
      v-if="!disabledMode"
      :disabled="Object.keys(availableFilters).length === modelValue?.length"
      icon="plus"
      type="positive"
      no-border
      class="t:my-8"
      @click="addFilter"
    >
      {{ $t('affiliates.offer_popup.add_filter') }}
    </UiButton>
    <UiSeparator />
    <FilterTabs
      :current-search="currentSearch"
      :reports-settings="reportsSettings"
      :search-by-list="searchByList"
      :is-searching-filter="isSearchingFilter"
      :disabled="disabledMode"
      @delete-current-search-group="clearReportsSearchFilter"
      @selected-search-filter="selectedSearchFilter"
    />
  </div>
</template>

<style lang="scss">
.dashboard-filter {
  &__row {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
  }
}
</style>
